import { useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import PropTypes from "prop-types";

import getLabel from "helpers/getLabel";
import validateTrackingNumber from "helpers/trackingNumberValidator";
import { IScacCode } from "types/Common";

const TrackingNumber = function (props: any) {
  const { trackingTypeKey, trackingKey, scacCodeKey, otherScacCodeKey, scacCodes, required } = props;
  const intl = useIntl();
  const {
    register,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const trackingType = getValues(trackingTypeKey);
  // Gets validation pattern, input information and placeholder based on the selected tracking type
  const trackingInfo = validateTrackingNumber(trackingType);

  const { ref: trackingInputRefFn, ...trackingInputProps } = register(trackingKey, {
    required,
    ...(trackingInfo.validationPattern && { pattern: trackingInfo.validationPattern }),
  });

  const trackingInputRef = useRef<any>(null);

  const isOtherSelected = () => {
    const selectedCode = getValues(scacCodeKey);
    if (!selectedCode) {
      return false;
    }
    return selectedCode === "other";
  };

  const handleCodeInputChange = (e: any) => {
    setValue(otherScacCodeKey, e.target.value);
    if (e.target.value?.length === 4 && trackingInputRef.current) {
      trackingInputRef.current.focus();
    }
  };

  const handleTrackingNumberPaste = (e: any) => {
    const enableScac = trackingType === "mbol";
    const pastedValue = e.clipboardData.getData("text");
    if (pastedValue.length < 4 || !enableScac) {
      return;
    }
    const firstFour = pastedValue.slice(0, 4);
    if (isNaN(firstFour)) {
      const pastedCodeData = scacCodes.find((item: IScacCode) => item.number.toLowerCase() === firstFour.toLowerCase());
      if (pastedCodeData) {
        setValue(scacCodeKey, pastedCodeData.number);
      } else {
        setValue(scacCodeKey, "other");
        setValue(otherScacCodeKey, firstFour);
      }
      trigger([scacCodeKey, otherScacCodeKey, trackingKey]);
      setTimeout(() => {
        setValue(trackingKey, pastedValue.slice(4));
      }, 0);
    }
  };

  return (
    <div className="tracking-number">
      <div className="d-flex flex-column">
        <label htmlFor={trackingKey} className={required ? "required" : ""}>
          <span>
            <FormattedMessage
              id="general.trackingNumber"
              values={{ trackingNumber: getLabel("Tracking number", "capitalize") }}
              defaultMessage="Tracking number"
            />
          </span>
        </label>
        <div className="d-flex align-items-center">
          {isOtherSelected() && (
            <input
              maxLength={4}
              type="text"
              placeholder="i.e ABCD"
              className={`form-control pt-10 pb-10 pl-14 pr-14  w-100 height-auto placeholder-light code-input ${
                errors?.shipment?.other_tracking_scac_code ? "invalid-input" : ""
              }`}
              id={otherScacCodeKey}
              disabled={trackingType === ""}
              {...register(otherScacCodeKey, {
                required,
                pattern: /^[a-zA-Z]{4}$/,
              })}
              onChange={handleCodeInputChange}
            />
          )}
          <input
            ref={(e) => {
              trackingInputRefFn(e);
              trackingInputRef.current = e;
            }}
            type="text"
            placeholder={
              trackingInfo.placeholder
                ? trackingInfo.placeholder
                : intl.formatMessage({ id: "new-shipment.trackingNumber.pleaseProvide", defaultMessage: "Please Provide" })
            }
            className={`form-control pt-10 pb-10 pl-14 pr-14  w-100 height-auto placeholder-light flex-grow-1 ${
              errors?.shipment?.tracking_number ? "invalid-input" : ""
            }`}
            id={trackingKey}
            disabled={trackingType === ""}
            onPaste={handleTrackingNumberPaste}
            {...trackingInputProps}
          />
        </div>
        {errors?.shipment?.tracking_number ? (
          <div className="d-block font-b3 invalid-feedback">
            {!getValues(trackingKey) ? (
              <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
            ) : null}
          </div>
        ) : null}
      </div>
      {trackingType === "" && (
        <div className="mt-2 text-secondary font-b3">
          <FormattedMessage
            id="new-shipment.trackingNumber.selectTrackingType"
            defaultMessage="Please select a tracking type"
          />
        </div>
      )}
      <div className="mt-2 text-secondary font-b3">{trackingInfo.infoMessage}</div>
    </div>
  );
};

export default TrackingNumber;

TrackingNumber.propTypes = {
  trackingTypeKey: PropTypes.string.isRequired,
  trackingKey: PropTypes.string.isRequired,
  scacCodeKey: PropTypes.string.isRequired,
  otherScacCodeKey: PropTypes.string.isRequired,
  scacCodes: PropTypes.instanceOf(Array).isRequired,
  required: PropTypes.bool,
};

TrackingNumber.defaultProps = {
  required: true,
};
